/**
 * Enhanced User Profile Dropdown with Comprehensive Usage Display
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Shield, Crown, BarChart3, MessageSquare, Calendar, RotateCcw, ArrowDownToLine } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/subscription/SubscriptionContext';
import { useAudience } from '../context/AudienceContext';
import { useBuildAudience } from '../context/BuildAudienceContext';
import { useAudienceData } from '../contexts/AudienceContext';
import { getZIndexClass } from '../config/zIndex';
import { checkResetAuthorization, resetMyUsage, switchToFreeTier } from '../services/adminService';

interface UserProfileDropdownProps {
  onClose: () => void;
}

interface UsageItemProps {
  icon: React.ReactNode;
  label: string;
  current: number;
  limit?: number;
  showProgress?: boolean;
  isUnlimited?: boolean;
}

const UsageItem: React.FC<UsageItemProps> = ({ 
  icon, 
  label, 
  current, 
  limit, 
  showProgress = false,
  isUnlimited = false
}) => {
  const percentage = limit && limit > 0 ? (current / limit) * 100 : 0;
  
  const getProgressColor = () => {
    if (percentage <= 60) return 'bg-emerald-500';
    if (percentage <= 80) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex items-center justify-between py-1">
      <div className="flex items-center space-x-2">
        <div className="text-gray-500">{icon}</div>
        <span className="text-xs text-gray-700">{label}</span>
      </div>
      <div className="flex items-center space-x-2">
        <span className="text-xs font-medium text-gray-900">
          {current}
          {limit !== undefined && !isUnlimited && `/${limit}`}
          {isUnlimited && '/∞'}
        </span>
        {showProgress && limit && limit > 0 && (
          <div className="w-12 bg-gray-200 rounded-full h-1.5">
            <div 
              className={`h-1.5 rounded-full transition-all duration-300 ${getProgressColor()}`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({ onClose }) => {
  const { user, isAdmin, logout } = useAuth();
  const { currentSubscription, usage, refreshSubscription } = useSubscription();
  const { resetAudienceData } = useAudience();
  const { resetBuildAudienceData } = useBuildAudience();
  const { resetAudienceData: resetAudienceListData } = useAudienceData();
  
  const [canResetUsage, setCanResetUsage] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isSwitchingToFree, setIsSwitchingToFree] = useState(false);
  const [resetMessage, setResetMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [switchMessage, setSwitchMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Check if user is authorized to reset usage
  useEffect(() => {
    const checkAuthorization = async () => {
      const authorized = await checkResetAuthorization();
      setCanResetUsage(authorized);
    };
    
    if (user) {
      checkAuthorization();
    }
  }, [user]);

  const handleLogout = async () => {
    // Reset all audience contexts before logout
    resetAudienceData();
    resetBuildAudienceData();
    resetAudienceListData();
    
    // Then logout
    await logout();
    onClose();
  };

  const handleLinkClick = () => {
    onClose();
  };

  const handleResetUsage = async () => {
    setIsResetting(true);
    setResetMessage(null);
    
    try {
      const result = await resetMyUsage();
      
      if (result.success) {
        setResetMessage({ type: 'success', text: 'Usage reset successfully!' });
        
        // Refresh subscription data after successful reset
        await refreshSubscription();
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setResetMessage(null);
        }, 3000);
      } else {
        setResetMessage({ type: 'error', text: result.error || 'Failed to reset usage' });
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          setResetMessage(null);
        }, 5000);
      }
    } catch (error) {
      setResetMessage({ type: 'error', text: 'An error occurred' });
      setTimeout(() => {
        setResetMessage(null);
      }, 5000);
    } finally {
      setIsResetting(false);
    }
  };

  const handleSwitchToFree = async () => {
    setIsSwitchingToFree(true);
    setSwitchMessage(null);
    
    try {
      const result = await switchToFreeTier();
      
      if (result.success) {
        setSwitchMessage({ type: 'success', text: result.message || 'Switched to free tier successfully!' });
        
        // Refresh subscription data after successful switch
        await refreshSubscription();
        
        // Clear success message after 5 seconds (longer message needs more time to read)
        setTimeout(() => {
          setSwitchMessage(null);
        }, 5000);
      } else {
        setSwitchMessage({ type: 'error', text: result.error || 'Failed to switch to free tier' });
        
        // Clear error message after 5 seconds
        setTimeout(() => {
          setSwitchMessage(null);
        }, 5000);
      }
    } catch (error) {
      setSwitchMessage({ type: 'error', text: 'An error occurred' });
      setTimeout(() => {
        setSwitchMessage(null);
      }, 5000);
    } finally {
      setIsSwitchingToFree(false);
    }
  };

  if (!user) return null;

  const isEnterprise = currentSubscription?.id === 'enterprise';
  const isFree = currentSubscription?.id === 'free';

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 ${getZIndexClass('popover')}`} 
        onClick={onClose}
      />
      
      {/* Dropdown */}
      <div className={`absolute left-0 bottom-full mb-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1 ${getZIndexClass('dialog')}`}>
        {/* User Info */}
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-900">Signed in as</p>
          <p className="text-xs text-gray-500 truncate">{user.email}</p>
          
          {/* Plan Info */}
          {currentSubscription && (
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs font-medium text-gray-600">
                Plan: {currentSubscription.name}
              </span>
              {isFree && <Crown className="h-3 w-3 text-primary" />}
              {isEnterprise && <Crown className="h-3 w-3 text-yellow-500" />}
            </div>
          )}
        </div>

        {/* Usage Display */}
        {usage && (
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-gray-900">Monthly Usage</h4>
              <div className="flex items-center text-xs text-gray-500">
                <Calendar className="h-3 w-3 mr-1" />
                {usage.daysUntilReset} days left
              </div>
            </div>
            
            <div className="space-y-2">
              {/* Simulations */}
              <UsageItem
                icon={<BarChart3 className="h-3 w-3" />}
                label="Simulations"
                current={usage.simulationsUsed}
                limit={usage.simulationsLimit}
                showProgress={true}
                isUnlimited={usage.simulationsLimit === -1}
              />
              
      
              
              {/* Group Chats */}
              <UsageItem
                icon={<MessageSquare className="h-3 w-3" />}
                label="Group Chats"
                current={usage.groupChatsUsed}
                limit={usage.groupChatsLimit}
                showProgress={true}
                isUnlimited={usage.groupChatsLimit === -1}
              />
              
              {/* Simulation Chats */}
              <UsageItem
                icon={<MessageSquare className="h-3 w-3" />}
                label="Sim Chats"
                current={usage.simulationChatsUsed}
                limit={usage.simulationChatsLimit}
                showProgress={true}
                isUnlimited={usage.simulationChatsLimit === -1}
              />
            </div>
            
            {/* Static Limits */}
            {usage.questionsPerSurvey && usage.optionsPerQuestion && (
              <div className="mt-3 pt-2 border-t border-gray-100">
                <p className="text-xs text-gray-500">
                  Limits: {usage.questionsPerSurvey === -1 ? '∞' : usage.questionsPerSurvey} questions/survey, {' '}
                  {usage.optionsPerQuestion === -1 ? '∞' : usage.optionsPerQuestion} options/question
                </p>
              </div>
            )}
          </div>
        )}
        
        {/* Actions */}
        <div className="py-1">
          {/* Subscription Link */}
          <Link 
            to="/subscription/pricing" 
            className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={handleLinkClick}
          >
            <Crown className="h-4 w-4 mr-3 text-primary" />
            {isFree ? 'Upgrade Plan' : 'Manage Subscription'}
          </Link>
          
          {/* Admin Dashboard */}
          {isAdmin && (
            <Link 
              to="/admin" 
              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
              onClick={handleLinkClick}
            >
              <Shield className="h-4 w-4 mr-3 text-indigo-600" />
              Admin Dashboard
            </Link>
          )}
          
          {/* Reset Usage Button - Only for authorized users */}
          {canResetUsage && (
            <button
              onClick={handleResetUsage}
              disabled={isResetting}
              className="flex w-full items-center px-4 py-2 text-sm text-blue-600 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCcw className={`h-4 w-4 mr-3 ${isResetting ? 'animate-spin' : ''}`} />
              {isResetting ? 'Resetting...' : 'Reset Usage'}
            </button>
          )}
          
          {/* Switch to Free Tier Button - Only for authorized users */}
          {canResetUsage && (
            <button
              onClick={handleSwitchToFree}
              disabled={isSwitchingToFree}
              className="flex w-full items-center px-4 py-2 text-sm text-purple-600 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownToLine className={`h-4 w-4 mr-3 ${isSwitchingToFree ? 'animate-pulse' : ''}`} />
              {isSwitchingToFree ? 'Switching...' : 'Switch to Free Tier'}
            </button>
          )}
          
          {/* Reset Message */}
          {resetMessage && (
            <div className={`mx-4 my-2 px-3 py-2 rounded text-xs ${
              resetMessage.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {resetMessage.text}
            </div>
          )}
          
          {/* Switch Message */}
          {switchMessage && (
            <div className={`mx-4 my-2 px-3 py-2 rounded text-xs ${
              switchMessage.type === 'success' 
                ? 'bg-green-50 text-green-800 border border-green-200' 
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}>
              {switchMessage.text}
            </div>
          )}
          
          {/* Sign Out */}
          <button
            onClick={handleLogout}
            className="flex w-full items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100 transition-colors"
          >
            <LogOut className="h-4 w-4 mr-3" />
            Sign out
          </button>
        </div>
      </div>
    </>
  );
};

export default UserProfileDropdown;